import { type FC } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslations } from 'next-intl';

import { SearchField } from '@features/filters';
import { AssignedUserSelector, ExportButton } from '@features/merchant';

import {
  initialMerchantsFilters,
  initialMerchantsSort,
  type RequestForm,
} from '@entities/merchant';

import {
  Box,
  Button,
  Collapse,
  Flex,
  Icons,
  LoadableContent,
} from '@shared/ui-kit';

import { isFiltersActive } from '../lib';

import { MarketplaceSection, MerchantSection } from './_components';

interface Props {
  isLoading: boolean;
  count: number;
}

const MerchantsFilters: FC<Props> = (props) => {
  const { isLoading = false, count = 0 } = props;

  const t = useTranslations();

  const { reset, control } = useFormContext<RequestForm>();
  const formValues = useWatch({ control });
  const { filter } = formValues;

  const hasFilters = isFiltersActive(initialMerchantsFilters, filter);

  const resetFilters = () => {
    reset({
      ...formValues,
      filter: {
        ...formValues.filter,
        ...initialMerchantsFilters,
      },
      sort: {
        ...formValues.sort,
        key: initialMerchantsSort.key,
      },
    });
  };

  return (
    <Box
      id="merchants-filters-form"
      as="form"
    >
      <LoadableContent isLoading={isLoading}>
        <Flex
          justify="space-between"
          alignItems="end"
          mb="4"
        >
          <Flex columnGap="8">
            <SearchField
              name="filter.search"
              control={control}
              placeholder={t('Merchant.Filters.searchPlaceholder')}
            />
            <AssignedUserSelector
              name="filter.assigned_to"
              control={control}
              defaultValue="disabled"
            />
          </Flex>
          <ExportButton isActive={Boolean(count) && !isLoading} />
        </Flex>
      </LoadableContent>

      <Collapse
        data-testid="merchants-filters-сollapse"
        defaultIsOpen={hasFilters}
        variant="link"
        title={t('Merchant.Filters.label')}
        additionalActions={
          <Button
            m="0"
            rightIcon={
              <Icons.Close
                w="2"
                h="2"
              />
            }
            variant="ghost"
            isDisabled={isLoading}
            onClick={resetFilters}
            visibility={hasFilters ? 'visible' : 'hidden'}
            transition="all .2s linear"
            colorScheme="datascopeRed"
          >
            {t('Filters.resetButton')}
          </Button>
        }
      >
        <LoadableContent isLoading={isLoading}>
          <Flex
            data-testid="merchants-filters-сollapsed"
            rowGap="4"
          >
            <Flex
              direction="column"
              rowGap="8"
            >
              <MerchantSection isLoading={isLoading} />
              <MarketplaceSection isLoading={isLoading} />
            </Flex>
          </Flex>
        </LoadableContent>
      </Collapse>
    </Box>
  );
};

export default MerchantsFilters;
